import React, { useContext } from 'react';
import { TopNav } from '../../TopNav';
import { RightDrawer } from '../../RightDrawer';
import { SecondaryNav } from '../../SecondaryNav';
import { ThirdNav } from '../../ThirdNav';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Settings } from '../../Settings';
import { Publish } from '../../Publish';
import { TopNavProvider } from '../../../providers/TopNavProvider';
import { isFromChromeExtension, PostEditorContextType, RightDrawerContext } from '../../../providers/AppProvider';
import { PostEditorContextEnum } from '../../../models/post-editor';
import { PostEditor } from '../../PostEditor';
import { PageLayout } from '../../../components/containers/PageLayout';
import { Discover } from 'modules/Discover';
import { Analyze } from '../../Analyze';
import { clearImpersonationStorage, isImpersonating } from '../../../helpers/impersonationHelpers';
import { useUserData } from '../../../hooks/useUserData';
import { Button } from '../../../components/actions/Button';
import { ButtonColorsEnum } from '../../../constants/button-colors';
import { NotFoundRedirect } from '../NotFoundRedirect';
import { WhatsNewPage } from '../../WhatsNew/WhatsNewPage';
import { WhatsNewModal } from '../../WhatsNew/WhatsNewModal';
import { PostEditorPage } from '../PostEditorPage';
import { TouchBackend } from 'react-dnd-touch-backend';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';
import { WhatInputType } from 'what-input';
import useWhatInput from 'react-use-what-input';
import { Privacy } from '../../Policy/Privacy';
import { TermsOfService } from '../../Policy/TermsOfService';
import { DevDashboard } from '../../DevDashboard';
import { DevDebugContext } from '../../../providers/DevDebugProvider';
import scssVars from '../../../assets/scss/exportedVars.module.scss';
import { OnboardingWrapper } from '../../Onboarding/OnboardingWrapper';
import { LimitReachedModal } from 'modules/PlanLimit/LimitReachedModal/LimitReachedModal.view';
import { useSubscriptionStatus } from 'hooks/useSubscriptionStatus';
import { BlockedAccess } from '../BlockedAccess';
import { SpamLockedModal } from '../SpamLockedModal';

export type UserRootPageViewProps = {
  //
  postEditorContext: PostEditorContextType;
  rightDrawerState: RightDrawerContext;
  isOpenLimitReachModalOpen: boolean;
  isSuspendedModalOpen: boolean;
};

const ImpersonatingBanner = (): JSX.Element => {
  const { userData } = useUserData();
  const onStopImpersonating = () => {
    clearImpersonationStorage();
    window.location.assign('/');
  };
  const style: React.CSSProperties = {
    textAlign: 'center',
    background: 'greenyellow',
    height: 30,
    zIndex: scssVars['z-index-preloader'],
    bottom: 0,
    fontSize: 18,
    position: 'fixed',
    width: '100%'
  };
  return (
    <div style={style}>
      <span>
        You are impersonating {userData?.firstName} - ({userData?.email}) - (id: {userData?.id})
      </span>{' '}
      <Button
        type={'button'}
        variant={'solid'}
        size={'xSmall'}
        color={ButtonColorsEnum.alert}
        action={onStopImpersonating}>
        Stop Impersonating
      </Button>
    </div>
  );
};

export const UserRootPageView = ({
  postEditorContext,
  rightDrawerState,
  isOpenLimitReachModalOpen,
  isSuspendedModalOpen
}: UserRootPageViewProps): JSX.Element => {
  const { allowedDevDashboardAccess } = useContext(DevDebugContext);
  const intentType: WhatInputType = useWhatInput('intent');
  const { isAccessBlocked } = useSubscriptionStatus();
  if (isFromChromeExtension) {
    // If we're from the chrome extension, we only want to render the post editor & related providers.
    return (
      <DndProvider backend={intentType === 'touch' ? TouchBackend : HTML5Backend}>
        <PostEditorPage />
        {postEditorContext.type !== PostEditorContextEnum.undefined && <PostEditor context={postEditorContext} />}
      </DndProvider>
    );
  }

  if (isAccessBlocked) {
    return (
      <TopNavProvider>
        <TopNav />
        {rightDrawerState.open ? <RightDrawer /> : null}
        <PageLayout>
          <Routes>
            <Route path="*" element={<BlockedAccess />}></Route>
          </Routes>
        </PageLayout>
      </TopNavProvider>
    );
  }

  return (
    <DndProvider backend={intentType === 'touch' ? TouchBackend : HTML5Backend}>
      <TopNavProvider>
        <OnboardingWrapper>
          <TopNav />
          {rightDrawerState.open ? <RightDrawer /> : null}
          <SecondaryNav />
          <ThirdNav />
          {isImpersonating() && <ImpersonatingBanner />}
          <main>
            <PageLayout>
              <Routes>
                <Route path={'/'} element={<Navigate to={'publish/queue'} replace />} />
                <Route path={'post-editor'} element={<PostEditorPage />} />
                <Route path={'settings/*'} element={<Settings />} />
                <Route path={'publish/*'} element={<Publish />} />
                <Route path={'discover/*'} element={<Discover />} />
                <Route path={'analyze/*'} element={<Analyze />} />
                <Route path={'whats-new'} element={<WhatsNewPage />} />
                <Route path={'no-org'} element={<h1>You are not part of any organization</h1>} />
                <Route path={'terms'} element={<TermsOfService />} />
                <Route path={'privacy'} element={<Privacy />} />
                {allowedDevDashboardAccess && <Route path={'dev'} element={<DevDashboard />} />}
                <Route path={'*'} element={<NotFoundRedirect to={'/'} />} />
              </Routes>
            </PageLayout>
          </main>
          {postEditorContext.type !== PostEditorContextEnum.undefined && <PostEditor context={postEditorContext} />}
          {isOpenLimitReachModalOpen && <LimitReachedModal />}
          {isSuspendedModalOpen && <SpamLockedModal />}
          <WhatsNewModal />
        </OnboardingWrapper>
      </TopNavProvider>
    </DndProvider>
  );
};
